import {
  applicantSection,
  businessSection,
  deMinimisAttachments,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A7. Wniosek o ulgę w zapłacie podatków lokalnych: odroczenie terminu płatności, rozłożenie na raty albo umorzenie
 * zaległości, odsetek lub opłaty prolongacyjnej — art. 67a § 1 pkt 1–3 i art. 67b Ordynacji podatkowej; pomoc de minimis
 * — art. 37 ustawy o postępowaniu w sprawach dotyczących pomocy publicznej. Brak wzoru ogólnopolskiego (ogólnopolski
 * jest tylko formularz informacji de minimis); układ jak wzór Gminy Wodzierady
 * (https://bip-v1-files.idcom-jst.pl/sites/46701/bip_sprawy/3339/wniosek_o_umorzenie_zaleglosci_podatkowej.pdf — pobrany
 * i odczytany 22.10.2026: rodzaj ulgi, kwota, tytuł zobowiązania, uzasadnienie, załączniki). Rodzaj ulgi jest wariantem
 * w BE; lista załączników rozgałęzia się według statusu (osoba fizyczna / przedsiębiorca / rolnik) jak w katalogu.
 * Wzór oświadczenia o stanie majątkowym ustala urząd — do potwierdzenia z wydziałem.
 */
const DEFERRAL: EbiCondition = { field: "rodzajUlgi", equals: "odroczenie" };
const INSTALMENTS: EbiCondition = { field: "rodzajUlgi", equals: "raty" };
const REMISSION: EbiCondition = { field: "rodzajUlgi", equals: "umorzenie" };
const PERSON: EbiCondition = { field: "status", in: ["osobaFizyczna", "rolnik"] };
const BUSINESS: EbiCondition = { field: "status", equals: "przedsiebiorca" };
const FARMER: EbiCondition = { field: "status", equals: "rolnik" };

const A7: IEbiFormDefinition = {
  code: "A7",
  version: 1,
  title: "Wniosek o odroczenie terminu płatności, rozłożenie na raty lub umorzenie zaległości podatkowej",
  category: EEbiFormCategory.Taxes,
  office: EEbiFormOffice.Municipality,
  description: "Ulga w zapłacie podatku od nieruchomości, rolnego, leśnego lub od środków transportowych — odroczenie, raty albo umorzenie.",
  keywords: ["umorzenie", "raty", "odroczenie", "ulga", "zaległość podatkowa", "odsetki", "podatek od nieruchomości", "de minimis"],
  organ: "Wójt / Burmistrz / Prezydent Miasta jako organ podatkowy",
  legalBasis: "art. 67a § 1 i art. 67b ustawy z dnia 29 sierpnia 1997 r. — Ordynacja podatkowa",
  request:
    "Na podstawie art. 67a § 1 ustawy z dnia 29 sierpnia 1997 r. — Ordynacja podatkowa wnoszę o udzielenie ulgi w zapłacie zobowiązania podatkowego wskazanej poniżej.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty skarbowej; pełnomocnictwo — 17 zł.",
  deadlineLabel: "do miesiąca",
  deadline: "Bez zbędnej zwłoki — do miesiąca, w sprawie szczególnie skomplikowanej do 2 miesięcy (art. 139 Ordynacji podatkowej).",
  variantField: "rodzajUlgi",
  variants: {
    odroczenie: { title: "Wniosek o odroczenie terminu płatności podatku lub zaległości podatkowej" },
    raty: { title: "Wniosek o rozłożenie na raty zapłaty podatku lub zaległości podatkowej" },
    umorzenie: { title: "Wniosek o umorzenie zaległości podatkowej, odsetek za zwłokę lub opłaty prolongacyjnej" },
  },
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "status",
      title: "Status wnioskodawcy",
      fields: [
        {
          code: "status",
          type: "radio",
          label: "Podatnik jest",
          required: true,
          options: [
            { value: "osobaFizyczna", label: "osobą fizyczną nieprowadzącą działalności gospodarczej" },
            { value: "przedsiebiorca", label: "przedsiębiorcą (prowadzi działalność gospodarczą)" },
            { value: "rolnik", label: "rolnikiem albo rybakiem (prowadzi działalność rolniczą lub rybacką)" },
          ],
        },
      ],
    },
    businessSection("Przedsiębiorca — podatnik", BUSINESS),
    {
      code: "ulga",
      title: "Wnoszę o",
      fields: [
        {
          code: "rodzajUlgi",
          type: "radio",
          label: "Rodzaj ulgi",
          required: true,
          options: [
            { value: "odroczenie", label: "odroczenie terminu płatności podatku albo zaległości podatkowej wraz z odsetkami za zwłokę" },
            { value: "raty", label: "rozłożenie na raty zapłaty podatku albo zaległości podatkowej wraz z odsetkami za zwłokę" },
            { value: "umorzenie", label: "umorzenie w całości lub w części zaległości podatkowej, odsetek za zwłokę lub opłaty prolongacyjnej" },
          ],
        },
        {
          code: "przedmiotUlgi",
          type: "radio",
          label: "Ulga dotyczy",
          required: true,
          visibleWhen: { any: [DEFERRAL, INSTALMENTS] },
          options: [
            { value: "podatek", label: "podatku, którego termin płatności jeszcze nie upłynął" },
            { value: "zaleglosc", label: "zaległości podatkowej wraz z odsetkami za zwłokę" },
          ],
        },
        {
          code: "umorzenieDotyczy",
          type: "checkboxes",
          label: "Umorzenie dotyczy",
          required: true,
          visibleWhen: REMISSION,
          options: [
            { value: "zaleglosc", label: "zaległości podatkowej" },
            { value: "odsetki", label: "odsetek za zwłokę" },
            { value: "oplataProlongacyjna", label: "opłaty prolongacyjnej" },
          ],
        },
        {
          code: "zakresUmorzenia",
          type: "radio",
          label: "Zakres umorzenia",
          required: true,
          visibleWhen: REMISSION,
          options: [
            { value: "calosc", label: "w całości" },
            { value: "czesc", label: "w części" },
          ],
        },
        {
          code: "podatki",
          type: "checkboxes",
          label: "Z tytułu",
          required: true,
          options: [
            { value: "nieruchomosci", label: "podatku od nieruchomości" },
            { value: "rolny", label: "podatku rolnego" },
            { value: "lesny", label: "podatku leśnego" },
            { value: "transport", label: "podatku od środków transportowych" },
            { value: "inne", label: "innego podatku lub opłaty lokalnej" },
          ],
        },
        {
          code: "podatkiInne",
          type: "text",
          label: "Inny podatek lub opłata",
          required: true,
          visibleWhen: { field: "podatki", includes: "inne" },
          maxLength: 200,
        },
        {
          code: "okres",
          type: "text",
          label: "Okres, którego dotyczy (rok podatkowy, raty)",
          required: true,
          width: "half",
          maxLength: 200,
          placeholder: "np. 2026 r., raty I–III",
        },
        { code: "kwota", type: "number", label: "Kwota objęta wnioskiem [zł]", required: true, decimals: 2, min: 0, width: "half" },
        {
          code: "proponowanyTermin",
          type: "date",
          label: "Proponowany termin płatności",
          required: true,
          visibleWhen: DEFERRAL,
          width: "half",
        },
        { code: "liczbaRat", type: "number", label: "Proponowana liczba rat", required: true, visibleWhen: INSTALMENTS, min: 2, max: 120, width: "half" },
        {
          code: "harmonogramRat",
          type: "textarea",
          label: "Proponowana wysokość i terminy płatności rat (nieobowiązkowo)",
          visibleWhen: INSTALMENTS,
          maxLength: 1000,
        },
        {
          ...feeInfo(
            "infoOplataProlongacyjna",
            "Przy odroczeniu terminu płatności albo rozłożeniu zapłaty na raty organ może ustalić opłatę prolongacyjną (art. 57 Ordynacji podatkowej i uchwała rady gminy).",
          ),
          visibleWhen: { any: [DEFERRAL, INSTALMENTS] },
        },
      ],
    },
    {
      code: "uzasadnienie",
      title: "Uzasadnienie",
      description:
        "Ulgę przyznaje się w przypadkach uzasadnionych ważnym interesem podatnika lub interesem publicznym (art. 67a § 1 Ordynacji podatkowej). Wniosek bez uzasadnienia i dokumentów kończy się wezwaniem do uzupełnienia.",
      fields: [
        {
          code: "uzasadnienie",
          type: "textarea",
          label: "Uzasadnienie wniosku",
          required: true,
          maxLength: 5000,
          hint: "Opisz okoliczności, które uniemożliwiają lub poważnie utrudniają zapłatę w terminie lub w całości (sytuacja materialna i rodzinna, zdarzenia losowe, sytuacja firmy). Dokumenty dołącz w kolejnym kroku.",
        },
      ],
    },
    replySection("Sposób odbioru decyzji"),
  ],
  attachments: [
    {
      code: "oswiadczenieMajatkowe",
      label: "Oświadczenie o stanie majątkowym (na druku urzędu)",
      visibleWhen: PERSON,
      required: true,
    },
    {
      code: "dokumentySytuacji",
      label: "Dokumenty potwierdzające sytuację materialną",
      hint: "Np. zaświadczenia o dochodach lub PIT, decyzje ośrodka pomocy społecznej, zaświadczenie z urzędu pracy, rachunki stałych opłat, umowy kredytów.",
      visibleWhen: PERSON,
      required: true,
      multiple: true,
    },
    ...deMinimisAttachments(BUSINESS),
    {
      code: "dokumentyFinansowe",
      label: "Dokumenty finansowe przedsiębiorcy — sprawozdania finansowe albo zeznania PIT za 3 lata, bieżący bilans lub rachunek zysków i strat, deklaracje ZUS DRA",
      hint: "Urzędy zwykle proszą o deklaracje ZUS DRA z ostatnich 6 miesięcy.",
      visibleWhen: BUSINESS,
      required: true,
      multiple: true,
    },
    {
      code: "deMinimisRolnictwoFormularz",
      label: "Formularz informacji przedstawianych przy ubieganiu się o pomoc de minimis w rolnictwie lub rybołówstwie",
      hint: "Aktualny wzór z rozporządzenia Rady Ministrów (formularz udostępnia urząd).",
      visibleWhen: FARMER,
      required: true,
    },
    {
      code: "deMinimisRolnictwoZaswiadczenia",
      label: "Zaświadczenia o pomocy de minimis w rolnictwie lub rybołówstwie otrzymanej w ciągu ostatnich 3 lat albo oświadczenie o jej wielkości lub nieotrzymaniu",
      visibleWhen: FARMER,
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default A7;
